import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { Stack, Text, Box, useTheme } from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { DirectionsCar, Email, Home, Person, Phone } from '@mui/icons-material';
import { useContext, useRef } from 'react';

import {
  LandingPageContainer,
  ChatTerms,
  BrandLogoChat,
  ChatInput,
  LandingTextSectionStack,
} from 'components/ChatBV/components';
import { CHATBV_GA4_EVENTS, SEARCH_TYPES } from 'components/ChatBV/constants';
import { ChipData } from 'components/ChatBV/types';

import { ChatChips } from './ChatChips';
import { ChatInputRef } from './ChatInput';

type LandingSectionProps = {
  showLanding: boolean;
  landingOpacity: number;
  isTransitioning: boolean;
  isLogoAnimating: boolean;
  logoAnimationPhase: 'center' | 'moving-up' | 'moving-left' | 'header';
  openReport: boolean;
  onSendMessage: (message: string) => void; // eslint-disable-line no-unused-vars
};

export const LandingSection = ({
  showLanding,
  landingOpacity,
  isTransitioning,
  isLogoAnimating,
  logoAnimationPhase,
  openReport,
  onSendMessage,
}: LandingSectionProps) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  const { trackEventGA4 } = useContext(AppConfig);
  const chatInputRef = useRef<ChatInputRef>(null);

  const ChatChipsData: ChipData[] = [
    {
      icon: <Person sx={{ color: 'primary.main', fontSize: '16px' }} />,
      title: 'Find a person',
      type: SEARCH_TYPES.PERSON,
    },
    {
      icon: <Phone sx={{ color: 'primary.main', fontSize: '16px' }} />,
      title: 'Search a phone',
      type: SEARCH_TYPES.PHONE,
    },
    {
      icon: <Home sx={{ color: 'primary.main', fontSize: '16px' }} />,
      title: 'View address info',
      type: SEARCH_TYPES.PROPERTY,
    },
    {
      icon: <Email sx={{ color: 'primary.main', fontSize: '16px' }} />,
      title: 'Check an email',
      type: SEARCH_TYPES.EMAIL,
    },
    {
      icon: <DirectionsCar sx={{ color: 'primary.main', fontSize: '20px' }} />,
      title: 'Search a vehicle',
      type: SEARCH_TYPES.VEHICLE,
    },
  ];

  const handleChipClick = (type: string) => {
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.suggestion.type,
      subtype: CHATBV_GA4_EVENTS.suggestion.subTypes.chip_suggestion_click,
    });
    chatInputRef.current?.handleChipClick(type);
  };

  if (!showLanding) {
    return null;
  }

  return (
    <LandingPageContainer
      landingOpacity={landingOpacity}
      isTransitioning={isTransitioning}
    >
      <Stack
        flexDirection="column"
        alignItems="center"
        height="100vh"
        paddingTop="10%"
        paddingBottom="0"
        position="relative"
      >
        <Stack width="100%" alignItems={isMobile ? 'flex-start' : 'center'}>
          <BrandLogoChat
            width="138px"
            height="21px"
            margin="20px 0"
            sx={{
              transition: 'all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              ...(isLogoAnimating &&
                logoAnimationPhase === 'moving-up' && {
                  transform: isMobile
                    ? 'translateY(-60px) scale(0.8)'
                    : 'translateY(-120px) scale(0.8)',
                }),
              ...(isLogoAnimating &&
                logoAnimationPhase === 'moving-left' && {
                  transform: isMobile
                    ? 'translateY(-60px) scale(0.8)'
                    : 'translateY(-120px) translateX(-400px) scale(0.4)',
                }),
              ...(isLogoAnimating &&
                logoAnimationPhase === 'header' && {
                  opacity: 0,
                }),
            }}
          />
          <Box
            width="100%"
            display="flex"
            justifyContent={isMobile ? 'start' : 'center'}
            sx={{
              transition: 'opacity 0.5s ease-in-out',
              opacity: isLogoAnimating ? 0 : 1,
            }}
          >
            <LandingTextSectionStack
              flexDirection="row"
              fontSize="2.625rem"
              fontWeight="bold"
              m={0}
              mb={2.25}
              flexWrap="nowrap"
              alignItems={isMobile ? 'start' : 'center'}
            >
              <Text
                fontSize="inherit"
                fontWeight="bold"
                m={0}
                mr={0.5}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Ask Anything.
              </Text>
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  [theme.breakpoints.down('md')]: {
                    ml: 0,
                    mt: 0.5,
                  },
                }}
              >
                <Text
                  fontSize="inherit"
                  fontWeight="bold"
                  m={0}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Find{' '}
                </Text>
                <Text
                  fontSize="inherit"
                  fontWeight="bold"
                  m={0}
                  color="primary.main"
                  sx={{ whiteSpace: 'nowrap' }}
                  ml={0.5}
                >
                  Anyone
                </Text>
              </Stack>
            </LandingTextSectionStack>
          </Box>
          <Text
            variant="body1"
            mb={isMobile ? 4.5 : 6}
            sx={{
              transition: 'opacity 0.5s ease-in-out',
              opacity: isLogoAnimating ? 0 : 1,
            }}
          >
            Your AI assistant for in-depth searches on people, phones,
            addresses, emails, and vehicles.
          </Text>
        </Stack>

        <ChatInput
          ref={chatInputRef}
          onSendMessage={onSendMessage}
          isReportOpen={openReport}
          isLanding={true}
          isAnimating={isLogoAnimating}
          animationPhase={logoAnimationPhase}
          isLoading={isLogoAnimating || isTransitioning}
        />

        <Stack
          width="100%"
          maxWidth="100%"
          margin="0 auto"
          sx={{
            [theme.breakpoints.up('md')]: {
              width: '100%',
            },
            [theme.breakpoints.down('md')]: {
              width: '90%',
            },
            transition: 'opacity 0.5s ease-in-out',
            opacity: isLogoAnimating ? 0 : 1,
          }}
        >
          <ChatChips chips={ChatChipsData} onChipClick={handleChipClick} />
        </Stack>
      </Stack>

      <ChatTerms
        isAnimating={isLogoAnimating}
        animationPhase={logoAnimationPhase}
        isLanding={showLanding}
      />
    </LandingPageContainer>
  );
};
